import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { printCalculationDocument } from "../../../utils/printCalculationDocument";
import {
  getCalculatorData,
  getM1CommercialCalculation,
  getM1CommercialFinalCalculation,
  saveCalculationWithNotes,
  CurrencyRate,
  ExplanationData,
  GreenRatingGroup,
  // ApiResponse,
} from "../../../services/enduser/calculatorService";
import MotionCard, { MotionButton } from "../../shared/MotionCard";
import ExplanationTooltip from "../../shared/ExplanationTooltip";
import TaxYearNotice from "../../shared/TaxYearNotice";
import { formatThousands, toRawNumber } from "../../../utils/numberInput";
import GreenRatingCalculatorModal from "../../shared/GreenRatingCalculatorModal";
import ActualCostCalculator, { ActualCostData } from "../../shared/ActualCostCalculator";
// CalculatorIcon removed - using emoji instead

// ממשק נתוני טופס - M1 מסחרי
interface FormData {
  manufacturing_date: string;
  vehicle_price_foreign: string;
  currency_type: string;
  exchange_rate: string;
  shipping_cost_foreign: string;
  insurance_cost_foreign: string;
  insurance_currency_type: string;
  insurance_exchange_rate: string;
  local_expenses_ils: string;
  has_origin_declaration: boolean;
  vehicle_weight_category:
    | "under_3500"
    | "between_3500_4500"
    | "over_4500"
    | "";
  green_rating_group: string;
  airbags_count: string;
}

// ממשק אבזור בטיחותי M1 - עדכון נובמבר 2025
interface SafetyEquipmentData {
  airbagsCount: number;
  systems: {
    blindSpotDetection: boolean; // 1 - זיהוי כלי רכב ב"שטח מת"
    blindSpotCollisionPrevention: boolean; // 2 - אקטיבית למניעת התנגשות צד
    reverseBraking: boolean; // 3 - בלימה אוטומטית לאחור
    driverDistraction: boolean; // 4 - התרעה היסח דעת הנהג
    childAlert: boolean; // 5 - התרעה ילדים ברכב
    doorOpenWarning: boolean; // 6 - אזהרת פתיחת דלת
    adaptiveLighting: boolean; // 7 - תאורה אדפטיבית
    junctionEmergencyBraking: boolean; // 8 - בלימת חירום בצמתים
  };
}

// קבועים לאבזור בטיחותי - עדכון נובמבר 2025 (נוהל משרד התחבורה)
const SAFETY_SYSTEMS_SCORES = {
  blindSpotDetection: 1.0, // זיהוי כלי רכב ב"שטח מת"
  blindSpotCollisionPrevention: 2.0, // אקטיבית למניעת התנגשות צד ב"שטח מת"
  reverseBraking: 1.0, // בלימה אוטומטית בנסיעה לאחור
  driverDistraction: 1.0, // התרעה במצב היסח דעת הנהג
  childAlert: 1.5, // התרעה על השארת ילדים ברכב
  doorOpenWarning: 1.5, // אזהרת פתיחת דלת
  adaptiveLighting: 1.0, // תאורה אדפטיבית/שליטה באורות גבוהים
  junctionEmergencyBraking: 1.5, // בלימת חירום לתנועה חוצה בצמתים
} as const;

// const SAFETY_LEVELS = [
//   { level: 0, minScore: 0, minAirbags: 0, discount: 0 },
//   { level: 1, minScore: 1, minAirbags: 6, discount: 250 },
//   { level: 2, minScore: 2, minAirbags: 6, discount: 600 },
//   { level: 3, minScore: 3, minAirbags: 6, discount: 1000 },
//   { level: 4, minScore: 4, minAirbags: 6, discount: 1600 },
//   { level: 5, minScore: 5, minAirbags: 6, discount: 1850 },
//   { level: 6, minScore: 6, minAirbags: 6, discount: 2050 },
//   { level: 7, minScore: 7, minAirbags: 6, discount: 2250 },
//   { level: 8, minScore: 8, minAirbags: 6, discount: 2400 },
// ] as const;
const STEP_KEYS = {
  manufacturing_date: "manufacturing_date",
  vehicle_price: "vehicle_price",
  currency_selection: "currency_selection",
  exchange_rate: "exchange_rate",
  shipping_cost: "shipping_cost",
  insurance_cost: "insurance_cost",
  insurance_currency: "insurance_currency",
  insurance_exchange_rate: "insurance_exchange_rate",
  local_expenses: "local_expenses",
  origin_declaration: "origin_declaration",
  vehicle_weight: "vehicle_weight",
  green_rating: "green_rating",
  consumer_price: "consumer_price_commercial",
} as const;

// מיפוי רמה → safety_features תואם (לשליחה ל-Backend ללא שינוי)
const SAFETY_LEVEL_TO_FEATURES: Record<number, { airbagsCount: number; systems: Record<string, boolean> }> = {
  0: { airbagsCount: 0, systems: { blindSpotDetection: false, blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  1: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: false, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  2: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: false, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  3: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: true, childAlert: false, doorOpenWarning: false, adaptiveLighting: false, junctionEmergencyBraking: false } },
  4: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: true, childAlert: false, doorOpenWarning: false, adaptiveLighting: true, junctionEmergencyBraking: false } },
  5: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: true, childAlert: true, doorOpenWarning: false, adaptiveLighting: true, junctionEmergencyBraking: false } },
  6: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: true, childAlert: true, doorOpenWarning: true, adaptiveLighting: true, junctionEmergencyBraking: false } },
  7: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: false, reverseBraking: true, driverDistraction: true, childAlert: true, doorOpenWarning: true, adaptiveLighting: true, junctionEmergencyBraking: true } },
  8: { airbagsCount: 6, systems: { blindSpotDetection: true, blindSpotCollisionPrevention: true, reverseBraking: true, driverDistraction: true, childAlert: true, doorOpenWarning: true, adaptiveLighting: true, junctionEmergencyBraking: true } },
};

const SAFETY_LEVEL_DISCOUNTS: Record<number, number> = {
  0: 0, 1: 250, 2: 600, 3: 1000, 4: 1600, 5: 1850, 6: 2050, 7: 2250, 8: 2400,
};

const M1CommercialCalculatorPage: React.FC = () => {
  const navigate = useNavigate();

  // ========================================
  // State Management
  // ========================================

  // שלב נוכחי (1 או 2 או "results")
  const [currentStep, setCurrentStep] = useState<number>(1);

  // נתוני הטופס
  const [formData, setFormData] = useState<FormData>({
    manufacturing_date: "",
    vehicle_price_foreign: "",
    currency_type: "EUR",
    exchange_rate: "",
    shipping_cost_foreign: "",
    insurance_cost_foreign: "",
    insurance_currency_type: "EUR",
    insurance_exchange_rate: "",
    local_expenses_ils: "",
    has_origin_declaration: false,
    vehicle_weight_category: "",
    green_rating_group: "",
    airbags_count: "",
  });

  // אבזור בטיחותי (רק למתחת ל-3.5 טון)
  const [safetyEquipmentData, setSafetyEquipmentData] =
    useState<SafetyEquipmentData>({
      airbagsCount: 0,
      systems: {
        blindSpotDetection: false,
        blindSpotCollisionPrevention: false,
        reverseBraking: false,
        driverDistraction: false,
        childAlert: false,
        doorOpenWarning: false,
        adaptiveLighting: false,
        junctionEmergencyBraking: false,
      },
    });

  // תוצאות מחישוב שלב 1
  const [basicCalculationResult, setBasicCalculationResult] =
    useState<any>(null);

  // מחיר לצרכן לשלב 2 (רק למתחת ל-3.5)
  const [consumerPrice, setConsumerPrice] = useState<string>("");

  // תוצאות סופיות
  const [finalCalculationResult, setFinalCalculationResult] =
    useState<any>(null);

  // ref לאזור התוצאה — מקור לשכפול בהדפסה החדשה (גרסת פיתוח)
  const printRef = useRef<HTMLDivElement>(null);

  // נתוני עזר מהשרת
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [explanations, setExplanations] = useState<
    Record<string, ExplanationData>
  >({});
  const [greenRatingGroups, setGreenRatingGroups] = useState<
    GreenRatingGroup[]
  >([]);

  // UI State
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isGreenRatingModalOpen, setIsGreenRatingModalOpen] =
    useState<boolean>(false);

  // שמירה
  const [userNotes, setUserNotes] = useState<string>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [commercialActualCostData, setCommercialActualCostData] = useState<ActualCostData | null>(null);
  // מצב בחירת אבזור בטיחותי
  const [safetyInputMode, setSafetyInputMode] = useState<"select" | "calculator">("select");
  const [selectedSafetyLevel, setSelectedSafetyLevel] = useState<number>(0);
  // ========================================
  // Effects
  // ========================================

  // טעינת נתוני עזר בטעינה ראשונית
  useEffect(() => {
    loadCalculatorData();
  }, []);

  useEffect(() => {
    if (formData.currency_type && currencyRates.length > 0) {
      const selectedCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.currency_type
      );
      if (selectedCurrency) {
        setFormData((prev) => ({
          ...prev,
          exchange_rate: Number(selectedCurrency.customs_rate).toFixed(4),
        }));
      }
    }
  }, [formData.currency_type, currencyRates]);

  useEffect(() => {
    if (formData.insurance_currency_type && currencyRates.length > 0) {
      const selectedInsuranceCurrency = currencyRates.find(
        (rate) => rate.currency_code === formData.insurance_currency_type
      );
      if (selectedInsuranceCurrency) {
        setFormData((prev) => ({
          ...prev,
          insurance_exchange_rate: Number(
            selectedInsuranceCurrency.customs_rate
          ).toFixed(4),
        }));
      }
    }
  }, [formData.insurance_currency_type, currencyRates]);

  // איפוס אבזור וציון ירוק כשמשנים משקל
  // איפוס אבזור וציון ירוק כשמשנים משקל
  useEffect(() => {
    if (formData.vehicle_weight_category !== "under_3500") {
      // איפוס אבזור בטיחותי
      setSafetyEquipmentData({
        airbagsCount: 0,
        systems: {
          blindSpotDetection: false,
          blindSpotCollisionPrevention: false,
          reverseBraking: false,
          driverDistraction: false,
          childAlert: false,
          doorOpenWarning: false,
          adaptiveLighting: false,
          junctionEmergencyBraking: false,
        },
      });
      // איפוס ציון ירוק
      setFormData((prev) => ({
        ...prev,
        green_rating_group: "",
      }));
    }
  }, [formData.vehicle_weight_category]);

  // ========================================
  // Helper Functions
  // ========================================

  const loadCalculatorData = async () => {
    try {
      const response = await getCalculatorData();
      if (response.res === "OK" && response.data) {
        setCurrencyRates(response.data.currency_rates || []);
        setExplanations(response.data.explanations || {});
        setGreenRatingGroups(response.data.green_rating_groups || []);
      }
    } catch (error) {
      console.error("Error loading calculator data:", error);
      setError("שגיאה בטעינת נתוני המחשבון");
    }
  };

  const handleNumberInputChange = (field: keyof FormData, value: string) => {
    const cleanValue = value.replace(/^0+(?=\d)/, "");
    setFormData((prev) => ({ ...prev, [field]: cleanValue }));
  };

  // חישוב ציון בטיחות
  const calculateSafetyScore = (): number => {
    return Object.entries(safetyEquipmentData.systems)
      .filter(([_, enabled]) => enabled)
      .reduce((sum, [systemKey, _]) => {
        return (
          sum +
          (SAFETY_SYSTEMS_SCORES[
            systemKey as keyof typeof SAFETY_SYSTEMS_SCORES
          ] || 0)
        );
      }, 0);
  };

  // חישוב רמת הנחה - ממוזער עם useMemo
  const safetyLevel = useMemo(() => {
    if (safetyInputMode === "select") {
      return selectedSafetyLevel;
    }
    // מצב מחשבון — חישוב מהצ'קבוקסים
    const score = calculateSafetyScore();
    const airbagsCount = safetyEquipmentData.airbagsCount;
    if (airbagsCount < 6) return 0;
    if (score >= 10) return 8;
    if (score >= 8) return 7;
    if (score >= 6) return 6;
    if (score >= 5) return 5;
    if (score >= 4) return 4;
    if (score >= 3) return 3;
    if (score >= 2) return 2;
    if (score >= 1) return 1;
    return 0;
  }, [safetyInputMode, selectedSafetyLevel, safetyEquipmentData]);

  // חישוב הנחה - ממוזער עם useMemo
  const safetyDiscount = useMemo(() => {
    return SAFETY_LEVEL_DISCOUNTS[safetyLevel] || 0;
  }, [safetyLevel]);
  // ========================================
  // Step 1 Handlers
  // ========================================

  const handleStep1Submit = async () => {
    // ולידציות
    if (!termsAccepted) {
      setError("יש לאשר את תנאי השימוש ומדיניות הפרטיות לפני ביצוע חישוב");
      return;
    }
    if (
      !formData.vehicle_price_foreign ||
      !formData.currency_type ||
      !formData.exchange_rate ||
      !formData.shipping_cost_foreign ||
      formData.local_expenses_ils === ""
    ) {
      setError("נא למלא את כל השדות החובה");
      return;
    }

    if (!formData.vehicle_weight_category) {
      setError("נא לבחור קטגוריית משקל רכב");
      return;
    }

    // בדיקה אם צריך ציון ירוק/אבזור (רק למתחת ל-3.5)
    if (formData.vehicle_weight_category === "under_3500") {
      // חובה לבחור ציון ירוק
      if (!formData.green_rating_group) {
        setError("נא לבחור קבוצת ציון ירוק");
        return;
      }
    }

    setError("");
    setLoading(true);

    try {
      // הכנת נתונים לשליחה
      const requestData: any = {
        manufacturing_date: formData.manufacturing_date,
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign),
        insurance_cost_foreign: parseFloat(
          formData.insurance_cost_foreign || "0"
        ),
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate: parseFloat(
          formData.insurance_exchange_rate || formData.exchange_rate
        ),
        local_expenses_ils: parseFloat(formData.local_expenses_ils || "0"),
        has_origin_declaration: formData.has_origin_declaration,
        vehicle_weight_category: formData.vehicle_weight_category,
      };
      // הוספת מספר כריות למסלול 2
      if (
        formData.vehicle_weight_category === "between_3500_4500" &&
        formData.airbags_count
      ) {
        requestData.airbags_count = parseInt(formData.airbags_count);
      }
      // הוספת ציון ירוק אם רלוונטי
      if (
        formData.vehicle_weight_category === "under_3500" &&
        formData.green_rating_group
      ) {
        requestData.green_rating_group = parseInt(formData.green_rating_group);
      }

      // הוספת אבזור בטיחותי אם רלוונטי
      if (formData.vehicle_weight_category === "under_3500") {
        // בחירה מהירה: ממיר רמה ל-features תואם, המחשבון: שולח כמו שהוא
        requestData.safety_features = safetyInputMode === "select"
          ? SAFETY_LEVEL_TO_FEATURES[selectedSafetyLevel] as SafetyEquipmentData
          : safetyEquipmentData;
      }

      const response = await getM1CommercialCalculation(requestData);

      if (response.res === "OK" && response.data) {
        setBasicCalculationResult(response.data);

        // קביעה האם צריך שלב 2
        if (response.data.calculation_info?.needs_step2) {
          // מסלול 3 - צריך מחיר לצרכן
          setCurrentStep(2);
        } else {
          // מסלולים 1 ו-2 - ישר לתוצאות
          setFinalCalculationResult(response.data);
          setCurrentStep(3); // דף תוצאות
        }
      } else {
        setError(response.msg || "שגיאה בחישוב");
      }
    } catch (error) {
      console.error("Error in step 1:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };
  // ========================================
  // Step 2 Handler - Final Calculation
  // ========================================

  const handleStep2Submit = async () => {
    if (!consumerPrice || !basicCalculationResult) {
      setError("נא למלא מחיר לצרכן");
      return;
    }

    const consumerPriceNum = parseFloat(consumerPrice);
    const calculatedPrice =
      basicCalculationResult.final_results?.final_total || 0;

    setError("");
    setLoading(true);

    try {
      const requestData: any = {
        vehicle_price_foreign: parseFloat(formData.vehicle_price_foreign),
        currency_type: formData.currency_type,
        exchange_rate: parseFloat(formData.exchange_rate),
        shipping_cost_foreign: parseFloat(formData.shipping_cost_foreign),
        insurance_cost_foreign: parseFloat(
          formData.insurance_cost_foreign || "0"
        ),
        insurance_currency_type:
          formData.insurance_currency_type || formData.currency_type,
        insurance_exchange_rate: parseFloat(
          formData.insurance_exchange_rate || formData.exchange_rate
        ),
        local_expenses_ils: parseFloat(formData.local_expenses_ils || "0"),
        has_origin_declaration: formData.has_origin_declaration,
        vehicle_weight_category: formData.vehicle_weight_category,
        consumer_price: consumerPriceNum,
        calculated_price_with_vat: calculatedPrice,
      };

      if (formData.green_rating_group) {
        requestData.green_rating_group = parseInt(formData.green_rating_group);
      }

      requestData.safety_features = safetyInputMode === "select"
        ? SAFETY_LEVEL_TO_FEATURES[selectedSafetyLevel] as SafetyEquipmentData
        : safetyEquipmentData;

      const response = await getM1CommercialFinalCalculation(requestData);

      if (response.res === "OK" && response.data) {
        setFinalCalculationResult(response.data);
        setCurrentStep(3);
      } else {
        setError(response.msg || "שגיאה בחישוב סופי");
      }
    } catch (error) {
      console.error("Error in step 2:", error);
      setError("שגיאה בתקשורת עם השרת");
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // Render Calculation Details Helper
  // ========================================

  const renderCalculationDetails = (calculation: any) => {
    const calc = calculation;

    // בדיקת בטיחות - ודא שיש intermediate_calculations ו-final_results
    if (!calc.intermediate_calculations || !calc.final_results) {
      return <div className="text-red-600 p-4">שגיאה: מבנה נתונים לא תקין</div>;
    }

    return (
      <>
        {/* מחיר לצרכן - רק לרכבים מתחת ל-3.5 טון */}
        {calc.input_data?.vehicle_weight_category === "under_3500" &&
          calc.input_data?.consumer_price && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">מחיר לצרכן (₪):</span>
              <span className="font-bold">
                {calc.input_data.consumer_price.toLocaleString()} ₪
              </span>
            </div>
          )}
        {/* 1. ערך למכס */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">1. ערך למכס (₪):</span>
          <span className="font-bold">
            {(
              calc.intermediate_calculations.total_value_for_customs || 0
            ).toLocaleString()}{" "}
            ₪
          </span>
        </div>

        {/* 2. מכס */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">2. מכס:</span>
          <span className="font-bold">
            {(calc.intermediate_calculations.customs_fee || 0).toLocaleString()}{" "}
            ₪
          </span>
        </div>

        {/* 3. שיעור מכס */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">3. שיעור מכס:</span>
          <span className="font-bold">
            {calc.intermediate_calculations.customs_rate_percent || 0}%
            {calc.intermediate_calculations.customs_exemption_reason && (
              <span className="text-green-600 mr-2">
                ({calc.intermediate_calculations.customs_exemption_reason})
              </span>
            )}
          </span>
        </div>

        {/* 4. מס קניה בסיסי - רק אם יש */}
        {calc.intermediate_calculations.base_purchase_tax !== undefined &&
          calc.intermediate_calculations.base_purchase_tax > 0 && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">
                4. מס קניה בסיסי (
                {calc.intermediate_calculations
                  .base_purchase_tax_rate_percent || 0}
                %):
              </span>
              <span className="font-bold">
                {(
                  calc.intermediate_calculations.base_purchase_tax || 0
                ).toLocaleString()}{" "}
                ₪
              </span>
            </div>
          )}

        {/* ציון ירוק - רק אם יש */}
        {calc.intermediate_calculations.green_rating_info && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="font-medium">
              5. ציון ירוק -{" "}
              {
                calc.intermediate_calculations.green_rating_info
                  .group_display_name
              }
              :
            </span>
            <span className="font-bold">
              {calc.intermediate_calculations.green_rating_adjustment < 0
                ? "-"
                : "+"}
              {Math.abs(
                calc.intermediate_calculations.green_rating_adjustment || 0
              ).toLocaleString()}{" "}
              ₪
            </span>
          </div>
        )}

        {/* אבזור בטיחותי - רק אם יש */}
        {calc.intermediate_calculations.safety_discount > 0 && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="font-medium">
              6. אבזור בטיחותי (רמה{" "}
              {calc.intermediate_calculations.safety_level}):
            </span>
            <span className="font-bold">
              -
              {(
                calc.intermediate_calculations.safety_discount || 0
              ).toLocaleString()}{" "}
              ₪
            </span>
          </div>
        )}

        {/* הנחת כריות אוויר - רק למסלול 2 */}
        {calc.intermediate_calculations.airbags_count !== null &&
          calc.intermediate_calculations.airbags_count !== undefined && (
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">
                 כריות אוויר ({calc.intermediate_calculations.airbags_count}{" "}
                כריות):
              </span>
              <span className="font-bold">
                {calc.intermediate_calculations.airbags_discount > 0 ? (
                  <>
                    -
                    {(
                      calc.intermediate_calculations.airbags_discount || 0
                    ).toLocaleString()}{" "}
                    ₪
                  </>
                ) : (
                  <>0 ₪ (אין הנחה)</>
                )}
              </span>
            </div>
          )}

        {/* מס קניה אחרי הנחות - רק אם יש מס קניה */}
        {calc.intermediate_calculations.purchase_tax_after_discounts !==
          undefined && (
          <div className="flex justify-between items-center py-2 border-b border-gray-200">
            <span className="font-medium">מס קניה אחרי הנחות:</span>
            <span className="font-bold">
              {(
                calc.intermediate_calculations.purchase_tax_after_discounts || 0
              ).toLocaleString()}{" "}
              ₪
            </span>
          </div>
        )}

        {/* מס יוקרה - רק אם יש */}
        {calc.luxury_tax_calculation && (
          <>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">
                מס יוקרה חל - מחיר מעל 300,000 ש"ח
              </span>
              <span className="font-bold">✓</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">7. שיעור מס יוקרה:</span>
              <span className="font-bold">
                {(calc.luxury_tax_calculation.luxury_tax_rate || 0).toFixed(2)}%
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-200">
              <span className="font-medium">8. סך מס יוקרה:</span>
              <span className="font-bold">
                {(
                  calc.luxury_tax_calculation.luxury_tax_amount || 0
                ).toLocaleString()}{" "}
                ₪
              </span>
            </div>
          </>
        )}

        {/* מס קניה סופי - רק אם יש מס קניה */}
        {calc.final_results.purchase_tax_final !== undefined &&
          calc.final_results.purchase_tax_final > 0 && (
            <>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="font-medium">מס קניה סופי:</span>
                <span className="font-bold">
                  {(
                    calc.final_results.purchase_tax_final || 0
                  ).toLocaleString()}{" "}
                  ₪
                </span>
              </div>

              {/* שיעור מס קניה אפקטיבי */}
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="font-medium">שיעור מס קניה אפקטיבי:</span>
                <span className="font-bold">
                  {(
                    calc.final_results.effective_purchase_tax_rate || 0
                  ).toFixed(2)}
                  %
                </span>
              </div>
            </>
          )}

        {/* בסיס למע"מ */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">בסיס למע"מ:</span>
          <span className="font-bold">
            {(calc.final_results.vat_base || 0).toLocaleString()} ₪
          </span>
        </div>

        {/* מע"מ */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">סך המע"מ (18%):</span>
          <span className="font-bold">
            {(calc.final_results.vat || 0).toLocaleString()} ₪
          </span>
        </div>

        {/* סך המיסים */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">סך המיסים:</span>
          <span className="font-bold">
            {(calc.final_results.total_taxes || 0).toLocaleString()} ₪
          </span>
        </div>

        {/* שיעור מיסוי */}
        <div className="flex justify-between items-center py-2 border-b border-gray-200">
          <span className="font-medium">שיעור מיסוי כולל:</span>
          <span className="font-bold">
            {(calc.final_results.tax_percentage || 0).toFixed(1)}%
          </span>
        </div>

        {/* תוצאה סופית */}
        <div className="flex justify-between items-center py-3 border-t-2 border-gray-800 px-4 mt-2">
          <span className="font-bold text-lg">עלות הרכב כולל מיסים:</span>
          <span className="font-bold text-2xl">
            {(calc.final_results.final_total || 0).toLocaleString()} ₪
          </span>
        </div>
      </>
    );
  };
  const handleGoBack = () => {
    // 🔥 אם יש calculation_info.has_luxury_tax = מסלול 3, חזור לשלב 2
    // אם אין = מסלולים 1-2, חזור לשלב 1
    const isRoute3 =
      finalCalculationResult?.calculation_info?.has_luxury_tax !== undefined;

    if (isRoute3) {
      setCurrentStep(2); // מסלול 3 - חזרה לשלב מחיר לצרכן
    } else {
      setCurrentStep(1); // מסלולים 1-2 - חזרה לשלב ראשון
    }

    setFinalCalculationResult(null); // ניקוי תוצאות
  };

  // הדפסה — מסמך נקי דרך המודול (דסקטופ: iframe מבודד, מובייל: טאב חדש)
  const handlePrint = () => {
    printCalculationDocument(printRef.current, {
      calculatorName: "מחשבון M1 בנזין/דיזל/היברידי - ייבוא מסחרי",
      calculationDate: new Date().toLocaleDateString("he-IL"),
      variant: "calculator",
    });
  };

  const handleSaveCalculation = async () => {
    if (!finalCalculationResult) return;

    setIsSaving(true);
    try {
      const saveRequest = {
        calculation_type: "M1_vehicle_commercial",
        input_data: finalCalculationResult.input_data,
        result_data: {
          ...finalCalculationResult,
          safety_equipment: safetyEquipmentData,
          safety_score: calculateSafetyScore(),
          safety_level: safetyLevel,
          safety_discount: safetyDiscount,
          commercial_actual_cost_data: commercialActualCostData,
        },
        notes: userNotes.trim(),
      };

      const response = await saveCalculationWithNotes(saveRequest);

      if (response.res === "OK") {
        setIsSaved(true);
        setTimeout(() => navigate("/enduser/dashboard"), 1500);
      } else {
        setError(response.msg || "שגיאה בשמירת החישוב");
      }
    } catch (error) {
      console.error("Error saving calculation:", error);
      setError("שגיאה בשמירת החישוב");
    } finally {
      setIsSaving(false);
    }
  };
  // ========================================
  // JSX Render
  // ========================================

  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8"
      dir="rtl"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="mx-auto mb-4 text-6xl"></div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            מחשבון M1 בנזין/דיזל/היברידי - ייבוא מסחרי
          </h1>
          <p className="text-gray-600">
            חישוב מיסים עבור רכב נוסעים M1 המיובא באופן מסחרי
          </p>
        </div>

        {/* Error Display */}
        {/* {error && (
          <MotionCard className="mb-6 p-4 bg-red-50 border border-red-200">
            <div className="flex items-center text-red-800">
              <span className="text-2xl ml-3">⚠️</span>
              <p className="font-medium">{error}</p>
            </div>
          </MotionCard>
        )} */}

        {/* ============================================ */}
        {/* שלב 1: נתוני רכב + משקל + אבזור/ציון ירוק */}
        {/* ============================================ */}
        {currentStep === 1 && (
          <>
            <MotionCard className="p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center ml-3 text-sm">
                  1
                </span>
                פרטי הרכב ומשקל
              </h2>
              <div className="mb-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-calculator"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      setError("");
                    }}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="terms-calculator"
                    className="text-sm text-gray-700 flex-1"
                  >
                    קראתי ואני מאשר/ת את{" "}
                    <Link
                      to="/terms-and-privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline font-semibold"
                    >
                      תנאי השימוש ומדיניות הפרטיות
                    </Link>{" "}
                    *
                  </label>
                </div>
              </div>
              <div className="space-y-6">
                {/* תאריך ייצור */}
                <div>
                  <label
                    htmlFor="manufacturing_date"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2"
                  >
                    תאריך ייצור הרכב <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.manufacturing_date}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="manufacturing_date"
                    aria-required="true"
                    type="date"
                    value={formData.manufacturing_date}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        manufacturing_date: e.target.value,
                      }))
                    }
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-orange-700 mt-1 font-medium">
                    ⚠️ יש לעקוב אחר הנחיות משרד התחבורה המתעדכנות מעת לעת
                  </p>
                </div>
                {/* מחיר רכב */}
                <div>
                  <label
                    htmlFor="vehicle_price_foreign"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    מחיר הרכב במטבע חוץ <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.vehicle_price}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="vehicle_price_foreign"
                    aria-required="true"
                    type="number"
                    min="0"
                    value={formData.vehicle_price_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "vehicle_price_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן מחיר רכב"
                  />
                </div>

                {/* בחירת מטבע */}
                <div>
                  <label
                    htmlFor="currency_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    סוג מטבע <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.vehicle_price}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="currency_type"
                    aria-required="true"
                    value={formData.currency_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        currency_type: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">בחר מטבע</option>
                    {currencyRates.map((rate) => (
                      <option
                        key={rate.currency_code}
                        value={rate.currency_code}
                      >
                        {rate.currency_name_hebrew} ({rate.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער מטבע */}
                {formData.currency_type && (
                  <div>
                    <label
                      htmlFor="exchange_rate"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      שער המרה <span className="text-red-500">*</span>
                      <span className="sr-only">(שדה חובה)</span>
                      <ExplanationTooltip
                        stepKey={STEP_KEYS.exchange_rate}
                        explanations={explanations}
                      />
                    </label>
                    <input
                      id="exchange_rate"
                      aria-required="true"
                      type="number"
                      min="0"
                      step="0.0001"
                      value={formData.exchange_rate}
                      onChange={(e) =>
                        handleNumberInputChange("exchange_rate", e.target.value)
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                      placeholder="שער המרה"
                    />
                  </div>
                )}

                {/* עלות משלוח */}
                <div>
                  <label
                    htmlFor="shipping_cost_foreign"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    עלות משלוח במטבע חוץ <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.shipping_cost}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="shipping_cost_foreign"
                    aria-required="true"
                    type="number"
                    min="0"
                    value={formData.shipping_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "shipping_cost_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן עלות משלוח"
                  />
                </div>

                {/* עלות ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_cost_foreign"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    עלות ביטוח במטבע חוץ
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_cost}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="insurance_cost_foreign"
                    type="number"
                    min="0"
                    value={formData.insurance_cost_foreign}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_cost_foreign",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן עלות ביטוח (אופציונלי)"
                  />
                </div>

                {/* מטבע ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_currency_type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    מטבע ביטוח
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_currency}
                      explanations={explanations}
                    />
                  </label>
                  <select
                    id="insurance_currency_type"
                    value={formData.insurance_currency_type}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        insurance_currency_type: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {currencyRates.map((rate) => (
                      <option
                        key={rate.currency_code}
                        value={rate.currency_code}
                      >
                        {rate.currency_name_hebrew} ({rate.currency_code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* שער חליפין ביטוח */}
                <div>
                  <label
                    htmlFor="insurance_exchange_rate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    שער חליפין ביטוח
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.insurance_exchange_rate}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="insurance_exchange_rate"
                    type="number"
                    min="0"
                    step="0.0001"
                    value={formData.insurance_exchange_rate}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "insurance_exchange_rate",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                    placeholder="שער המרה"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    שער מומלץ מתעדכן אוטומטית
                  </p>
                </div>
                {/* הוצאות מקומיות */}
                <div>
                  <label
                    htmlFor="local_expenses_ils"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    הוצאות מקומיות (₪) <span className="text-red-500">*</span>
                    <span className="sr-only">(שדה חובה)</span>
                    <ExplanationTooltip
                      stepKey={STEP_KEYS.local_expenses}
                      explanations={explanations}
                    />
                  </label>
                  <input
                    id="local_expenses_ils"
                    aria-required="true"
                    type="number"
                    min="0"
                    value={formData.local_expenses_ils}
                    onChange={(e) =>
                      handleNumberInputChange(
                        "local_expenses_ils",
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן הוצאות מקומיות (ניתן להזין 0)"
                  />
                </div>

                {/* הצהרת מקור */}
                <div className="flex items-center space-x-2 space-x-reverse">
                  <input
                    type="checkbox"
                    id="has_origin_declaration"
                    checked={formData.has_origin_declaration}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        has_origin_declaration: e.target.checked,
                      }))
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label
                    htmlFor="has_origin_declaration"
                    className="text-sm font-medium text-gray-700"
                  >
                    קיימת הצהרת מקור (פטור ממכס)
                  </label>
                </div>
              </div>
            </MotionCard>

            {/* בחירת קטגוריית משקל - 3 אפשרויות */}
            <MotionCard className="p-6 mb-6 bg-yellow-50 border-2 border-yellow-300">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                ⚖️ קטגוריית משקל הרכב *
                <ExplanationTooltip
                  stepKey={STEP_KEYS.vehicle_weight}
                  explanations={explanations}
                />
              </h3>

              <div className="space-y-3">
                {/* מתחת ל-3.5 טון */}
                <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                  <input
                    type="radio"
                    name="weight_category"
                    value="under_3500"
                    checked={formData.vehicle_weight_category === "under_3500"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle_weight_category: e.target.value as any,
                      }))
                    }
                    className="mt-1 w-4 h-4 text-blue-600"
                  />
                  <div className="mr-3">
                    <div className="font-bold text-gray-900">
                      מתחת ל-3.5 טון
                    </div>
                    {/* <div className="text-sm text-gray-600 mt-1">
                      מס קניה 83% + ציון ירוק + אבזור בטיחותי M1 + מס יוקרה אם מעל 300,000 ש"ח
                    </div> */}
                  </div>
                </label>

                {/* בין 3.5-4.5 טון */}
                <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                  <input
                    type="radio"
                    name="weight_category"
                    value="between_3500_4500"
                    checked={
                      formData.vehicle_weight_category === "between_3500_4500"
                    }
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle_weight_category: e.target.value as any,
                      }))
                    }
                    className="mt-1 w-4 h-4 text-blue-600"
                  />
                  <div className="mr-3">
                    <div className="font-bold text-gray-900">
                      בין 3.5 ל-4.5 טון
                    </div>
                    {/* <div className="text-sm text-gray-600 mt-1">
                      מס קניה 72% (ללא ציון ירוק, אבזור או מס יוקרה)
                    </div> */}
                  </div>
                </label>

                {/* מעל 4.5 טון */}
                <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                  <input
                    type="radio"
                    name="weight_category"
                    value="over_4500"
                    checked={formData.vehicle_weight_category === "over_4500"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        vehicle_weight_category: e.target.value as any,
                      }))
                    }
                    className="mt-1 w-4 h-4 text-blue-600"
                  />
                  <div className="mr-3">
                    <div className="font-bold text-gray-900">מעל 4.5 טון</div>
                    <div className="text-sm text-gray-600 mt-1">
                      ובתנאי שנפח הצילינדרים שלו עולה על 1500 סמ"ק במנוע בנזין
                      רגיל ומעל 1500 סמ"ק במנוע דיזל רגיל.
                    </div>
                  </div>
                </label>
              </div>
            </MotionCard>
            {/* כריות אוויר - רק למסלול 2 (3.5-4.5 טון) */}
            {formData.vehicle_weight_category === "between_3500_4500" && (
              <MotionCard className="p-6 mb-6 bg-blue-50 border-2 border-blue-300">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                   מספר כריות אוויר
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    מספר כריות אוויר ברכב *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={formData.airbags_count}
                    onChange={(e) =>
                      handleNumberInputChange("airbags_count", e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן מספר כריות אוויר"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    כריות אוויר סטנדרטיות (נהג, נוסע, צד וכו')
                  </p>
                </div>
              </MotionCard>
            )}
            {/* ציון ירוק - רק למתחת ל-3.5 טון */}
            {formData.vehicle_weight_category === "under_3500" && (
              <MotionCard className="p-6 mb-6 bg-green-50 border-2 border-green-300">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  🌱 ציון ירוק
                  <ExplanationTooltip
                    stepKey={STEP_KEYS.green_rating}
                    explanations={explanations}
                  />
                </h3>

                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      בחר קבוצת ציון ירוק *
                    </label>
                    <select
                      value={formData.green_rating_group}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          green_rating_group: e.target.value,
                        }))
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">בחר קבוצה</option>
                      {greenRatingGroups.map((group) => (
                        <option key={group.id} value={group.group_number}>
                          {group.group_display_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <MotionButton
                    onClick={() => setIsGreenRatingModalOpen(true)}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
                    aria-label="פתיחת מחשבון ציון ירוק"
                  >
                    📊 פתח מחשבון ציון ירוק
                  </MotionButton>
                </>
              </MotionCard>
            )}

            {/* אבזור בטיחותי M1 - רק למתחת ל-3.5 טון */}
            {formData.vehicle_weight_category === "under_3500" && (
              <MotionCard className="p-6 mb-6 bg-orange-50 border-2 border-orange-300">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                  🛡️ אבזור בטיחותי M1
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  הנחה אפשרית עד 2,400 ש"ח לפי רמת אבזור. ההנחה תשוקלל במחיר הסופי.
                </p>

                {/* מצב בחירה מהירה */}
                {safetyInputMode === "select" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      בחר רמת אבזור בטיחותי (0–8)
                    </label>
                    <select
                      value={selectedSafetyLevel}
                      onChange={(e) => setSelectedSafetyLevel(parseInt(e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent mb-3"
                    >
                      <option value="0">רמה 0 — ללא הנחה (0 ₪)</option>
                      <option value="1">רמה 1 — הנחה 250 ₪</option>
                      <option value="2">רמה 2 — הנחה 600 ₪</option>
                      <option value="3">רמה 3 — הנחה 1,000 ₪</option>
                      <option value="4">רמה 4 — הנחה 1,600 ₪</option>
                      <option value="5">רמה 5 — הנחה 1,850 ₪</option>
                      <option value="6">רמה 6 — הנחה 2,050 ₪</option>
                      <option value="7">רמה 7 — הנחה 2,250 ₪</option>
                      <option value="8">רמה 8 — הנחה 2,400 ₪</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => setSafetyInputMode("calculator")}
                      className="text-sm text-orange-600 underline hover:text-orange-800"
                    >
                      לא יודע את הרמה? פתח מחשבון אבזור בטיחותי
                    </button>
                  </div>
                )}

                {/* מצב מחשבון מפורט */}
                {safetyInputMode === "calculator" && (
                  <div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        מספר כריות אוויר
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="20"
                        value={safetyEquipmentData.airbagsCount}
                        onChange={(e) =>
                          setSafetyEquipmentData((prev) => ({
                            ...prev,
                            airbagsCount: parseInt(e.target.value) || 0,
                          }))
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="הזן מספר כריות"
                      />
                    </div>
                    <div className="space-y-3">
                      <h4 className="font-bold text-gray-900 mb-3">
                        מערכות בטיחות (ציון כולל: {calculateSafetyScore().toFixed(1)})
                      </h4>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={safetyEquipmentData.systems.blindSpotDetection}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, blindSpotDetection: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600"
                        />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת זיהוי כלי רכב ב"שטח מת"</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.blindSpotCollisionPrevention}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, blindSpotCollisionPrevention: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת אקטיבית למניעת התנגשות צד ב"שטח מת"</div><div className="text-xs text-gray-500">ציון: 2.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.reverseBraking}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, reverseBraking: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">בלימה אוטומטית בנסיעה לאחור</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.driverDistraction}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, driverDistraction: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת התרעה במצב של היסח דעת הנהג</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.childAlert}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, childAlert: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת התרעה על השארת ילדים ברכב</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.doorOpenWarning}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, doorOpenWarning: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת אזהרת פתיחת דלת</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.adaptiveLighting}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, adaptiveLighting: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת תאורה אדפטיבית / שליטה באורות גבוהים</div><div className="text-xs text-gray-500">ציון: 1.0</div></div>
                      </label>
                      <label className="flex items-start p-3 border rounded-lg hover:bg-orange-100 cursor-pointer transition-colors">
                        <input type="checkbox" checked={safetyEquipmentData.systems.junctionEmergencyBraking}
                          onChange={(e) => setSafetyEquipmentData((prev) => ({ ...prev, systems: { ...prev.systems, junctionEmergencyBraking: e.target.checked } }))}
                          className="mt-1 w-4 h-4 text-blue-600" />
                        <div className="mr-3"><div className="font-medium text-gray-900">מערכת בלימת חירום לתנועה חוצה בצמתים</div><div className="text-xs text-gray-500">ציון: 1.5</div></div>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSafetyInputMode("select")}
                      className="mt-4 text-sm text-orange-600 underline hover:text-orange-800"
                    >
                      ← חזור לבחירה מהירה
                    </button>
                  </div>
                )}

                {/* תיבת סיכום — תמיד מוצגת כשיש הנחה */}
                {safetyLevel > 0 && (
                  <div className="mt-4 p-3 bg-orange-100 border border-orange-300 rounded-lg text-sm text-orange-800 font-medium">
                    רמת אבזור: {safetyLevel} | הנחה צפויה: {safetyDiscount.toLocaleString()} ₪
                  </div>
                )}
              </MotionCard>
            )}
            {/* Error Display - ליד כפתור השליחה */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center text-red-800">
                  <span className="text-2xl ml-3">⚠️</span>
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}
            {/* כפתור המשך לחישוב */}
            <div className="flex gap-4">
              <MotionButton
                onClick={() =>
                  navigate("/enduser/calculators/commercial-import")
                }
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
              >
                ← חזור
              </MotionButton>

              <MotionButton
                onClick={handleStep1Submit}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 ml-2"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    מחשב...
                  </span>
                ) : (
                  "המשך לחישוב →"
                )}
              </MotionButton>
            </div>
          </>
        )}

        {/* Modal ציון ירוק */}
        <GreenRatingCalculatorModal
          isOpen={isGreenRatingModalOpen}
          onClose={() => setIsGreenRatingModalOpen(false)}
          onSelectGroup={(groupNumber) => {
            setFormData((prev) => ({
              ...prev,
              green_rating_group: groupNumber.toString(),
            }));
            setIsGreenRatingModalOpen(false);
          }}
          currentGroupNumber={
            formData.green_rating_group
              ? parseInt(formData.green_rating_group)
              : undefined
          }
        />
        {/* ============================================ */}
        {/* שלב 2: מחיר לצרכן - רק למסלול 3 */}
        {/* ============================================ */}
        {currentStep === 2 && basicCalculationResult && (
          <>
            <MotionCard className="p-6 mb-6 bg-blue-50 border-2 border-blue-300">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center ml-3 text-sm">
                  2
                </span>
                מחיר לצרכן
              </h2>

              <div className="space-y-6">
                {/* הצגת המחיר המחושב */}
                <div className="bg-white rounded-lg p-4 border-2 border-blue-300">
                  <h3 className="font-bold text-gray-900 mb-2">
                    💰 המחיר שחושב עד כה (כולל מע"מ):
                  </h3>
                  <p className="text-3xl font-bold text-blue-600">
                    {basicCalculationResult.final_results?.final_total?.toLocaleString() ||
                      "0"}{" "}
                    ₪
                  </p>
                  <p className="text-sm text-gray-600 mt-2">
                    זהו המחיר המינימלי שחישבה המערכת
                  </p>
                </div>

                {/* הזנת מחיר לצרכן */}
                <div>
                  <label
                    htmlFor="consumer_price"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    מחיר לצרכן (₪) *
                    {/* <ExplanationTooltip text="המחיר שבו תמכור את הרכב" /> */}
                  </label>
                  <input
                    id="consumer_price"
                    type="text"
                    inputMode="numeric"
                    aria-required="true"
                    value={formatThousands(consumerPrice)}
                    onChange={(e) =>
                      setConsumerPrice(toRawNumber(e.target.value))
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הזן מחיר לצרכן"
                  />
                </div>
              </div>
            </MotionCard>

            {/* כפתורים */}
            <div className="flex gap-4">
              <MotionButton
                onClick={() => {
                  setCurrentStep(1);
                  setConsumerPrice("");
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-3 rounded-lg font-bold"
              >
                ← חזור
              </MotionButton>

              <MotionButton
                onClick={handleStep2Submit}
                disabled={loading || !consumerPrice}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-5 w-5 ml-2"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                        fill="none"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    מחשב...
                  </span>
                ) : (
                  "חשב תוצאות סופיות →"
                )}
              </MotionButton>
            </div>
          </>
        )}
        {/* ============================================ */}
        {/* שלב 3: תוצאות סופיות */}
        {/* ============================================ */}
        {currentStep === 3 && finalCalculationResult && (
          <div className="space-y-6">
            {/* אזור התוצאה הניתן להדפסה (לא כולל "דייק לי" וכרטיס השמירה) */}
            <div ref={printRef} className="space-y-6">
            <TaxYearNotice />
            {/* סיכום נתוני קלט */}
            <MotionCard className="p-6 bg-blue-50 border-2 border-blue-300">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                📋 סיכום נתוני קלט
              </h2>

              {(() => {
                // 🔥 זיהוי מבנה - מסלול 3 או מסלולים 1-2
                const inputData = finalCalculationResult.system_calculation
                  ? finalCalculationResult.system_calculation.input_data
                  : finalCalculationResult.input_data;

                return (
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">מחיר רכב:</span>
                      <span className="font-bold">
                        {inputData.vehicle_price_foreign.toLocaleString()}{" "}
                        {inputData.currency_type}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">שער המרה:</span>
                      <span className="font-bold">
                        {inputData.exchange_rate.toFixed(4)}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">עלות משלוח:</span>
                      <span className="font-bold">
                        {inputData.shipping_cost_foreign.toLocaleString()}{" "}
                        {inputData.currency_type}
                      </span>
                    </div>
                    {/* הוצאות מקומיות */}
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">הוצאות מקומיות:</span>
                      <span className="font-bold">
                        {(inputData.local_expenses_ils || 0).toLocaleString()} ₪
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">קטגוריית משקל:</span>
                      <span className="font-bold">
                        {inputData.vehicle_weight_category === "under_3500" &&
                          "מתחת ל-3.5 טון"}
                        {inputData.vehicle_weight_category ===
                          "between_3500_4500" && "בין 3.5-4.5 טון"}
                        {inputData.vehicle_weight_category === "over_4500" &&
                          "מעל 4.5 טון"}
                      </span>
                    </div>

                    <div className="flex justify-between py-2 border-b border-blue-200">
                      <span className="font-medium">הצהרת מקור:</span>
                      <span className="font-bold">
                        {inputData.has_origin_declaration ? "כן ✓" : "לא"}
                      </span>
                    </div>

                    {/* מחיר לצרכן - רק במסלול 3 */}
                    {finalCalculationResult.user_calculation && (
                      <div className="flex justify-between py-2 border-b border-blue-200">
                        <span className="font-medium">מחיר יבואן:</span>
                        <span className="font-bold">
                          {finalCalculationResult.user_calculation.price_used.toLocaleString()}{" "}
                          ₪
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </MotionCard>
            {/* ============================================ */}
            {/* לוגיקה מותנית: מסלול 3 או מסלולים 1-2 */}
            {/* ============================================ */}
            {(() => {
              // סיכום נתוני קלט
              // const inputData = finalCalculationResult.input_data;

              return (
                <>
                  {/* סיכום נתוני קלט */}

                  {/* ============================================ */}
                  {/* תוצאות החישוב - טבלה אחת בלבד */}
                  {/* ============================================ */}

                  <MotionCard className="p-6 bg-green-50 border-2 border-green-300">
                    <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                      
                      {finalCalculationResult.input_data
                        ?.vehicle_weight_category === "over_4500" && (
                        <span className="text-sm font-normal text-gray-600">
                          (רכב מעל 4.5 טון - פטור ממס קניה)
                        </span>
                      )}
                      {finalCalculationResult.input_data
                        ?.vehicle_weight_category === "between_3500_4500" && (
                        <span className="text-sm font-normal text-gray-600">
                          (רכב 3.5-4.5 טון - מס קניה 72%)
                        </span>
                      )}
                    </h2>

                    <div className="space-y-2 text-sm">
                      {renderCalculationDetails(finalCalculationResult)}
                    </div>
                  </MotionCard>
                </>
              );
            })()}
            {/* דייק לי את התוצאה */}
            <ActualCostCalculator
              mode="commercial"
              vehicleCurrency={formData.currency_type}
              vehicleExchangeRate={formData.exchange_rate}
              vehiclePriceForeign={formData.vehicle_price_foreign}
              shippingCostForeign={formData.shipping_cost_foreign}
              insuranceCurrency={formData.insurance_currency_type}
              insuranceExchangeRate={formData.insurance_exchange_rate}
              insuranceCostForeign={formData.insurance_cost_foreign}
              localExpensesIls={formData.local_expenses_ils}
              totalTaxes={finalCalculationResult?.final_results?.total_taxes ?? 0}
              onDataChange={setCommercialActualCostData}
              explanations={explanations}
              initialData={commercialActualCostData}
            />
            </div>
            {/* שמירה והערות */}
            <MotionCard className="p-6 print-exclude">
              <h3 className="font-bold text-lg mb-4 text-gray-800">
                💾 שמירת החישוב
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  הערות נוספות (אופציונלי):
                </label>
                <textarea
                  value={userNotes}
                  onChange={(e) =>
                    e.target.value.length <= 1000 &&
                    setUserNotes(e.target.value)
                  }
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="הוסף הערות על החישוב, פרטים נוספים על הרכב וכו'..."
                />
                <div className="text-sm text-gray-500 mt-1">
                  {userNotes.length}/1000 תווים
                </div>
              </div>

              {/* הצגת שגיאות או הצלחה */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}

              {isSaved && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
                  ✅ החישוב נשמר בהצלחה! מעביר לדף הבית...
                </div>
              )}

              {/* כפתורים - מסודרים כמו בתמונה */}
              <div className="flex justify-between items-center mt-6">
                {/* כפתור שמאל: חזור לעריכה */}
                <MotionButton
                  onClick={handleGoBack}
                  disabled={isSaving || isSaved}
                  className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white"
                >
                  ← חזור לעריכה
                </MotionButton>

                {/* כפתור מרכז: הדפס */}
                <button
                  onClick={handlePrint}
                  disabled={isSaving || isSaved}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-gray-300"
                >
                  🖨️ הדפס
                </button>

                {/* כפתור ימין: שמור וחזור לדף הבית */}
                <button
                  onClick={handleSaveCalculation}
                  disabled={isSaving || isSaved}
                  className={`px-6 py-3 rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    isSaved
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  }`}
                >
                  {isSaving && "⏳ שומר..."}
                  {isSaved && "✓ נשמר - חזרה לדף הבית"}
                  {!isSaving && !isSaved && "💾 שמור וחזור לדף הבית"}
                </button>
              </div>
            </MotionCard>
          </div>
        )}

        {/* Footer Links */}
        <div className="text-center mt-8 pt-6 border-t border-gray-200">
          <div className="flex justify-center gap-6 text-sm text-gray-500">
            <Link
              to="/terms-and-privacy"
              className="hover:text-blue-600 transition-colors"
            >
              תנאי שימוש ופרטיות
            </Link>
            <span>|</span>
            <Link
              to="/accessibility-statement"
              className="hover:text-blue-600 transition-colors"
            >
              הצהרת נגישות
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default M1CommercialCalculatorPage;
